import { Controller } from '@hotwired/stimulus'

export default class extends Controller<HTMLElement> {
  static values = {
    optionErrorMessage: String,
    optionErrorSelector: String,
    optionSelector: String,
    optionValidationSelector: String
  }

  declare readonly hasOptionErrorMessageValue: boolean
  declare readonly hasOptionErrorSelectorValue: boolean
  declare readonly hasOptionSelectorValue: boolean
  declare readonly hasOptionValidationSelectorValue: boolean
  declare readonly optionErrorMessageValue: string
  declare readonly optionErrorSelectorValue: string
  declare readonly optionSelectorValue: string
  declare readonly optionValidationSelectorValue: string

  validate (event: Event): void {
    const form = this.form
    if (form === null) return
    this.syncOptionsValidity(form)

    if (!form.reportValidity()) {
      this.stop(event)
      return
    }

    this.stop(event)
    this.openModal(event)
  }

  private stop (event: Event): void {
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  private hasRequiredOptions (form: HTMLFormElement): boolean {
    if (!this.hasOptionSelectorValue) return true

    const validationControl = this.optionValidationControl(form)
    if (validationControl?.dataset.controlResponseConfigurationFormTarget === 'textBoxValidation') {
      return true
    }

    return Array.from(form.querySelectorAll<HTMLElement>(this.optionSelectorValue))
      .some((option) => this.optionIsVisible(option))
  }

  private optionIsVisible (option: HTMLElement): boolean {
    if (option.classList.contains('hidden')) return false

    return option.closest('.hidden') === null
  }

  private syncOptionsValidity (form: HTMLFormElement): void {
    const isValid = this.hasRequiredOptions(form)
    this.optionValidationControl(form)?.setCustomValidity(isValid ? '' : this.optionErrorMessage)

    if (this.hasOptionErrorSelectorValue) {
      form.querySelector<HTMLElement>(this.optionErrorSelectorValue)?.classList.toggle('hidden', isValid)
    }
  }

  private openModal (event: Event): void {
    const modalElement = this.element.closest<HTMLElement>('[data-controller~="modal"]')
    if (modalElement === null) return

    const modalController = this.application.getControllerForElementAndIdentifier(modalElement, 'modal') as ModalController | null
    modalController?.open(event)
  }

  private optionValidationControl (form: HTMLFormElement): HTMLInputElement | null {
    if (!this.hasOptionValidationSelectorValue) return null

    return form.querySelector<HTMLInputElement>(this.optionValidationSelectorValue)
  }

  private get optionErrorMessage (): string {
    if (this.hasOptionErrorMessageValue) return this.optionErrorMessageValue

    return ''
  }

  private get form (): HTMLFormElement | null {
    if (this.element instanceof HTMLButtonElement) return this.element.form

    return this.element.closest('form')
  }
}

interface ModalController {
  open: (event: Event) => void
}
